import React, { useEffect, useState } from 'react';
import { useGetList, useNotify, useTranslate } from 'react-admin';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel,
  FormLabel, InputLabel, MenuItem, Radio, RadioGroup, Select, Switch, TextField, Typography,
} from '@mui/material';
import { apiClient, type ApiError } from '../../utils/apiClient';
import { errorLine } from './labels';
import type { Release, RolloutTargetType } from './types';

type TargetType = RolloutTargetType;
type Channel = 'production' | 'pilot';
type Policy = 'one_at_a_time_per_site' | 'all_at_once';

export const URGENT_REASON_MIN = 10;
export const SITE_CONCURRENCY_MAX = 100;
const SITE_CONCURRENCY_PICKS = [1, 5, 10];
const WHOLE_NUMBER = /^[0-9]{1,3}$/;

export function siteConcurrencyOf(text: string): number | null {
  if (!WHOLE_NUMBER.test(text)) return null;
  const value = Number(text);
  return value >= 1 && value <= SITE_CONCURRENCY_MAX ? value : null;
}

interface Props {
  release: Release;
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export const RolloutDialog: React.FC<Props> = ({ release, open, onClose, onCreated }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [targetType, setTargetType] = useState<TargetType>('kiosk');
  const [siteId, setSiteId] = useState('');
  const [kioskIds, setKioskIds] = useState<string[]>([]);
  const [groupId, setGroupId] = useState('');
  const [channel, setChannel] = useState<Channel>('production');
  const [policy, setPolicy] = useState<Policy>('one_at_a_time_per_site');
  const [siteConcurrencyText, setSiteConcurrencyText] = useState('1');
  const [notBefore, setNotBefore] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [urgentReason, setUrgentReason] = useState('');
  const [journalConfirmed, setJournalConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const sites = useGetList('sites', { pagination: { page: 1, perPage: 500 }, sort: { field: 'name', order: 'ASC' } });
  const kiosks = useGetList('kiosk-devices', { pagination: { page: 1, perPage: 100 }, sort: { field: 'name', order: 'ASC' }, filter: { status: 'active' } });
  const groups = useGetList('kiosk-groups', { pagination: { page: 1, perPage: 100 }, sort: { field: 'name', order: 'ASC' } });
  const r = (key: string) => translate(`resources.kiosk-releases.rollout.${key}`);

  const reset = () => {
    setTargetType('kiosk');
    setSiteId('');
    setKioskIds([]);
    setGroupId('');
    setChannel('production');
    setPolicy('one_at_a_time_per_site');
    setSiteConcurrencyText('1');
    setNotBefore('');
    setUrgent(false);
    setUrgentReason('');
    setJournalConfirmed(false);
    setBusy(false);
    setProblem(null);
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  const chosenKiosks = (kiosks.data || []).filter((kiosk: any) => kioskIds.includes(kiosk.id));
  const chosenGroup = (groups.data || []).find((group: any) => group.id === groupId);
  const allAtOnceAllowed =
    (targetType === 'kiosk' && kioskIds.length > 0 && chosenKiosks.length === kioskIds.length && chosenKiosks.every((kiosk: any) => kiosk.inTestGroup === true)) ||
    (targetType === 'group' && chosenGroup !== undefined && chosenGroup.isTestGroup === true);
  const effectivePolicy: Policy = allAtOnceAllowed ? policy : 'one_at_a_time_per_site';
  useEffect(() => {
    if (!allAtOnceAllowed) setPolicy('one_at_a_time_per_site');
  }, [allAtOnceAllowed]);

  const targetIds = targetType === 'all' ? [] : targetType === 'site' ? (siteId === '' ? [] : [siteId]) : targetType === 'group' ? (groupId === '' ? [] : [groupId]) : kioskIds;
  const reasonOk = !urgent || urgentReason.trim().length >= URGENT_REASON_MIN;
  const journalOk = !release.journalBreaking || journalConfirmed;
  const siteConcurrency = effectivePolicy === 'all_at_once' ? 1 : siteConcurrencyOf(siteConcurrencyText);
  const ready = !busy && (targetType === 'all' || targetIds.length > 0) && reasonOk && journalOk && siteConcurrency !== null;

  const submit = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await apiClient.post(`/api/backend/kiosk-releases/${release.id}/rollouts`, {
        targetType,
        targetIds,
        channel,
        policy: effectivePolicy,
        siteConcurrency,
        notBefore: notBefore === '' ? null : new Date(notBefore).toISOString(),
        urgent,
        urgentReason: urgent ? urgentReason.trim() : null,
        confirmJournalBreaking: release.journalBreaking && journalConfirmed,
      });
      notify(r('created'), { type: 'success' });
      reset();
      onCreated();
      onClose();
    } catch (error) {
      setProblem(errorLine(error as ApiError, translate));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{r('title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormControl>
            <FormLabel>{r('who')}</FormLabel>
            <RadioGroup row value={targetType} onChange={(event) => setTargetType(event.target.value as TargetType)}>
              {(['all', 'site', 'kiosk', 'group'] as TargetType[]).map((value) => <FormControlLabel key={value} value={value} control={<Radio />} label={r(`targets.${value}`)} />)}
            </RadioGroup>
          </FormControl>
          {targetType === 'site' && (
            <FormControl fullWidth>
              <InputLabel id="rollout-site">{r('sites')}</InputLabel>
              <Select labelId="rollout-site" label={r('sites')} value={siteId} onChange={(event) => setSiteId(String(event.target.value))} inputProps={{ 'data-testid': 'rollout-site' }}>
                {(sites.data || []).map((site: any) => <MenuItem key={site.id} value={site.id}>{site.name}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          {targetType === 'kiosk' && (
            <FormControl fullWidth>
              <InputLabel id="rollout-kiosks">{r('kiosks')}</InputLabel>
              <Select
                labelId="rollout-kiosks"
                label={r('kiosks')}
                multiple
                value={kioskIds}
                onChange={(event) => setKioskIds(typeof event.target.value === 'string' ? event.target.value.split(',') : (event.target.value as string[]))}
                inputProps={{ 'data-testid': 'rollout-kiosks' }}
              >
                {(kiosks.data || []).map((kiosk: any) => <MenuItem key={kiosk.id} value={kiosk.id}>{`${kiosk.name} (${kiosk.siteName})`}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          {targetType === 'group' && (
            <FormControl fullWidth>
              <InputLabel id="rollout-groups">{r('groups')}</InputLabel>
              <Select
                labelId="rollout-groups"
                label={r('groups')}
                value={groupId}
                onChange={(event) => setGroupId(String(event.target.value))}
                inputProps={{ 'data-testid': 'rollout-groups' }}
              >
                {(groups.data || []).map((group: any) => (
                  <MenuItem key={group.id} value={group.id} disabled={group.memberCount === 0}>
                    {`${group.isTestGroup ? `${group.name} (${translate('resources.kiosk-groups.testGroup')})` : group.name}, ${translate('resources.kiosk-groups.kioskCount', { smart_count: group.memberCount })}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl>
            <FormLabel>{r('channel')}</FormLabel>
            <RadioGroup row value={channel} onChange={(event) => setChannel(event.target.value as Channel)}>
              {(['production', 'pilot'] as Channel[]).map((value) => <FormControlLabel key={value} value={value} control={<Radio />} label={r(`channels.${value}`)} />)}
            </RadioGroup>
          </FormControl>
          <FormControl>
            <FormLabel>{r('pace')}</FormLabel>
            <RadioGroup value={effectivePolicy} onChange={(event) => setPolicy(event.target.value as Policy)}>
              {((allAtOnceAllowed ? ['one_at_a_time_per_site', 'all_at_once'] : ['one_at_a_time_per_site']) as Policy[]).map((value) => <FormControlLabel key={value} value={value} control={<Radio />} label={r(`policies.${value}`)} />)}
            </RadioGroup>
          </FormControl>
          {effectivePolicy === 'one_at_a_time_per_site' && (
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {SITE_CONCURRENCY_PICKS.map((value) => (
                  <Button key={value} variant={siteConcurrencyText === String(value) ? 'contained' : 'outlined'} onClick={() => setSiteConcurrencyText(String(value))} sx={{ minWidth: 48 }}>{String(value)}</Button>
                ))}
              </Box>
              <TextField
                label={r('siteConcurrency')}
                value={siteConcurrencyText}
                onChange={(event) => setSiteConcurrencyText(event.target.value.trim())}
                error={siteConcurrency === null}
                helperText={r('siteConcurrencyHelp')}
                inputProps={{ 'data-testid': 'rollout-site-concurrency', inputMode: 'numeric', maxLength: 3 }}
                sx={{ flex: 1 }}
              />
            </Box>
          )}
          <TextField type="datetime-local" label={r('notBefore')} value={notBefore} onChange={(event) => setNotBefore(event.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ 'data-testid': 'rollout-not-before' }} />
          <FormControlLabel control={<Switch checked={urgent} onChange={(event) => setUrgent(event.target.checked)} inputProps={{ 'aria-label': r('urgent') }} />} label={r('urgent')} />
          {urgent && (
            <TextField
              label={r('urgentReason')}
              value={urgentReason}
              onChange={(event) => setUrgentReason(event.target.value)}
              multiline
              minRows={2}
              error={urgentReason.trim().length > 0 && !reasonOk}
              inputProps={{ 'data-testid': 'rollout-urgent-reason', maxLength: 500 }}
            />
          )}
          <Typography variant="body2" color="text.secondary">{r('fixedLine')}</Typography>
          {release.journalBreaking && (
            <Alert severity="warning">
              <Typography variant="body2">{r('journalWarning')}</Typography>
              <FormControlLabel control={<Checkbox checked={journalConfirmed} onChange={(event) => setJournalConfirmed(event.target.checked)} />} label={r('journalConfirm')} />
            </Alert>
          )}
          {problem !== null && <Alert severity="error">{problem}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>{translate('ra.action.cancel')}</Button>
        <Button onClick={submit} variant="contained" disabled={!ready} startIcon={busy ? <CircularProgress size={16} /> : undefined} sx={{ textTransform: 'none' }}>
          {r('submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
